import { useParams, Link } from 'react-router-dom';
import { articleBySlug, latestArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleBySlug(slug) : undefined;

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 md:pt-28 pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-[800px] mx-auto text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章可能已被删除或链接已失效</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.category)?.name || article.category;
  const relatedArticles = latestArticles(3).filter((a) => a.slug !== article.slug);

  return (
    <Layout>
      <article className="pt-24 md:pt-28 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-[800px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-primary-500 font-medium bg-primary-50 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 阅读约{article.readTime}
              </span>
            </div>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${article.category}`}
                  className="flex items-center gap-2 text-sm text-secondary-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回{categoryName}
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到Twitter">
                    <i className="ri-twitter-x-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-xl text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                        <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mt-1">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </Layout>
  );
}